import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';
import DataVerificationTooltip from './DataVerificationTooltip';
import VendorFormModal from './VendorFormModal';
import '../styles/admin.css';

const AdminVendors = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('asc');

    useEffect(() => {
        fetchVendors();
    }, []);

    // Refresh vendors when returning from edit/add pages
    useEffect(() => {
        if (location.pathname === '/admin/vendors') {
            fetchVendors();
        }
    }, [location]);

    const fetchVendors = async () => {
        try {
            setLoading(true);
            const response = await httpClient.get(`${ENDPOINTS.ADMIN_API}/vendors?per_page=100`);
            setVendors(response?.data?.data || response?.data || []);
        } catch (error) {
            console.error('Error fetching vendors:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this vendor? All their services will also be deleted.')) return;

        try {
            await httpClient.delete(`${ENDPOINTS.ADMIN_API}/vendors/${id}`);
            fetchVendors();
            alert('Vendor deleted successfully');
        } catch (error) {
            alert(error.response?.data?.message || 'Error deleting vendor');
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedVendor(null);
        setModalOpen(true);
    };

    const openEditModal = (vendor) => {
        setModalMode('edit');
        setSelectedVendor(vendor);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedVendor(null);
        setModalMode('add');
    };

    const handleModalSuccess = () => {
        handleModalClose();
        fetchVendors();
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    const getSortArrow = (column) => {
        if (sortBy !== column) return ' ⇅';
        return sortOrder === 'asc' ? ' ↑' : ' ↓';
    };

    const sortData = (data) => {
        return [...data].sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (sortBy === 'city_id') {
                aVal = a.city?.name || '';
                bVal = b.city?.name || '';
            }

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
                return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });
    };

    const filteredVendors = vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.phone.includes(searchTerm)
    );

    const sortedVendors = sortData(filteredVendors);

    return (
        <div className="admin-section">
            <div className="section-header">
                <h2>👥 Vendors Management</h2>
                <button
                    className="btn btn-primary"
                    onClick={openAddModal}
                >
                    ➕ Add Vendor
                </button>
            </div>

            <div className="search-bar" style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    placeholder="Search vendors by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '6px',
                        border: '1px solid var(--admin-border)',
                        background: 'var(--admin-sidebar)',
                        color: 'var(--admin-text)',
                        fontSize: '0.95rem',
                    }}
                />
            </div>

            <div className="admin-table">
                <table>
                    <thead>
                        <tr>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>ID{getSortArrow('id')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Name{getSortArrow('name')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>Email{getSortArrow('email')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('phone')}>Phone{getSortArrow('phone')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('business_name')}>Business Name{getSortArrow('business_name')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('city_id')}>City{getSortArrow('city_id')}</th>
                            <th>Verified</th>
                            <th>Data Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                                    Loading...
                                </td>
                            </tr>
                        ) : filteredVendors.length === 0 ? (
                            <tr>
                                <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                                    No vendors found.
                                </td>
                            </tr>
                        ) : (
                            sortedVendors.map(vendor => (
                                <tr key={vendor.id}>
                                    <td>{vendor.id}</td>
                                    <td>{vendor.name}</td>
                                    <td>{vendor.email}</td>
                                    <td>{vendor.phone}</td>
                                    <td>{vendor.business_name || '-'}</td>
                                    <td>{vendor.city?.name || '-'}</td>
                                    <td>{vendor.is_verified ? '✅ Yes' : '❌ No'}</td>
                                    <td>
                                        <DataVerificationTooltip dataVerification={vendor.data_verification}>
                                            {vendor.data_verification?.is_complete ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ fontSize: '0.9rem' }}>✅</span>
                                                    <span style={{ color: '#10b981', fontWeight: '600', fontSize: '0.85rem' }}>Complete</span>
                                                    {vendor.data_verification?.completion_percentage && (
                                                        <span style={{ color: 'var(--admin-text-secondary)', fontSize: '0.8rem' }}>
                                                            {vendor.data_verification.completion_percentage}%
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#f97316', fontWeight: '600', fontSize: '0.85rem' }}>
                                                    Incomplete
                                                    {vendor.data_verification?.completion_percentage && (
                                                        <span style={{ color: 'var(--admin-text-secondary)', fontSize: '0.8rem', marginLeft: '4px' }}>
                                                            {vendor.data_verification.completion_percentage}%
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </DataVerificationTooltip>
                                    </td>
                                    <td style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => openEditModal(vendor)}
                                            style={{ borderRadius: '6px', padding: '6px 12px', fontSize: '0.85rem' }}
                                        >
                                            ✏️ Edit
                                        </button>
                                        <button
                                            className="btn btn-sm"
                                            onClick={() => handleDelete(vendor.id)}
                                            style={{ 
                                                borderRadius: '6px', 
                                                padding: '6px 12px', 
                                                fontSize: '0.85rem',
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            🗑️ Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <VendorFormModal
                isOpen={modalOpen}
                mode={modalMode}
                vendorData={selectedVendor}
                onClose={handleModalClose}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default AdminVendors;
